import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, OnChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CalendarSource } from '@vendasta/meetings';
import { MeetingSchedulerStoreService } from '../../data-providers/meeting-scheduler-store.service';
import { MatRadioChange } from '@angular/material/radio';
import {
  CALENDAR_SOURCE,
  POSTHOG_ACTIONS,
  POSTHOG_CATEGORIES,
  POSTHOG_KEYS,
  getCalendarProviderDisplayName,
} from '../../constants';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { isInSettingsMode } from '../../utils';

@Component({
  selector: 'meeting-scheduler-calendar-selection',
  templateUrl: './calendar-selection.component.html',
  styleUrl: './calendar-selection.component.scss',
  standalone: false,
})
export class CalendarSelectionComponent implements OnInit, OnDestroy, OnChanges {
  selectedCalendarProvider: string;
  @Input() enableTracking: boolean;
  @Input() isGoogleConfigured: boolean; // OAuth connection status
  @Input() isMicrosoftConfigured: boolean; // OAuth connection status
  @Output() calendarProviderSelected = new EventEmitter<string>();
  readonly googleTooltipText: string;
  readonly microsoftTooltipText: string;
  private subscription: Subscription;

  // Track which provider is awaiting connection for tracking
  private awaitingConnectionForTracking: string | null = null;

  private readonly AWAITING_CONNECTION_KEY = 'meeting-scheduler-awaiting-connection-tracking';

  constructor(
    private readonly translate: TranslateService,
    private readonly storeService: MeetingSchedulerStoreService,
    private readonly analyticsService: ProductAnalyticsService,
    private readonly alertService: SnackbarService,
    private readonly route: ActivatedRoute,
  ) {
    this.googleTooltipText = this.translate.instant(
      'MEETING_SCHEDULER.MEETING_INTEGRATIONS.GOOGLE_SIGN_IN_TOOLTIP_TEXT',
    );
    this.microsoftTooltipText = this.translate.instant(
      'MEETING_SCHEDULER.MEETING_INTEGRATIONS.MICROSOFT_SIGN_IN_TOOLTIP_TEXT',
    );
  }

  ngOnInit(): void {
    // Restore awaiting connection state from localStorage
    this.restoreAwaitingConnectionState();

    this.subscription = this.storeService
      .loadUserPreferences()
      .pipe(take(1))
      .subscribe((preferences) => {
        if (preferences.calendarIntegration === CalendarSource.CALENDAR_SOURCE_MICROSOFT) {
          this.selectedCalendarProvider = CALENDAR_SOURCE.MICROSOFT;
        } else {
          this.selectedCalendarProvider = CALENDAR_SOURCE.GOOGLE;
        }
        this.emitSelectedProvider();
      });
  }

  ngOnChanges(): void {
    if (this.awaitingConnectionForTracking) {
      const providerJustConnected =
        (this.awaitingConnectionForTracking === CALENDAR_SOURCE.GOOGLE && this.isGoogleConfigured === true) ||
        (this.awaitingConnectionForTracking === CALENDAR_SOURCE.MICROSOFT && this.isMicrosoftConfigured === true);

      if (providerJustConnected) {
        this.trackCalendarChange(this.awaitingConnectionForTracking);
        this.setAwaitingConnectionState(null); // Clear after tracking
      }
    }
  }

  private restoreAwaitingConnectionState(): void {
    try {
      const stored = localStorage.getItem(this.AWAITING_CONNECTION_KEY);
      if (stored) {
        this.awaitingConnectionForTracking = stored;
      }
    } catch (error) {
      console.error('Failed to restore awaiting connection state:', error);
    }
  }

  private setAwaitingConnectionState(provider: string | null): void {
    this.awaitingConnectionForTracking = provider;
    try {
      if (provider) {
        localStorage.setItem(this.AWAITING_CONNECTION_KEY, provider);
      } else {
        localStorage.removeItem(this.AWAITING_CONNECTION_KEY);
      }
    } catch (error) {
      console.error('Failed to save awaiting connection state:', error);
    }
  }

  private isProviderConnected(provider: string): boolean {
    // Check OAuth connection status
    // Google Calendar and Google Meet share the same OAuth connection
    // Outlook Calendar and Microsoft Teams share the same OAuth connection
    if (provider === CALENDAR_SOURCE.GOOGLE) {
      return this.isGoogleConfigured;
    } else if (provider === CALENDAR_SOURCE.MICROSOFT) {
      return this.isMicrosoftConfigured;
    }
    return false;
  }

  private trackCalendarChange(provider: string): void {
    // Track calendar provider changes only in settings mode
    // This ensures we differentiate between initial setup and ongoing changes
    // Only tracks when the provider is actually usable (connected)
    if (isInSettingsMode(this.route)) {
      this.analyticsService.trackEvent(
        POSTHOG_KEYS.CALENDAR_PROVIDER_CHANGED,
        POSTHOG_CATEGORIES.USER,
        POSTHOG_ACTIONS.CLICK,
        undefined,
        { calendar_or_meeting_provider: getCalendarProviderDisplayName(provider) },
      );
    }
  }

  async onCalendarProviderChange(event: MatRadioChange) {
    // Update UI state immediately so the correct sign-in button shows without
    // waiting for the API call. This prevents clicking the old provider's OAuth button.
    const previousProvider = this.selectedCalendarProvider;
    this.selectedCalendarProvider = event.value;
    this.emitSelectedProvider();

    try {
      await this.storeService.updateUserPreferences({
        calendarIntegration:
          event.value === CALENDAR_SOURCE.MICROSOFT
            ? CalendarSource.CALENDAR_SOURCE_MICROSOFT
            : CalendarSource.CALENDAR_SOURCE_GOOGLE,
      });

      // Original tracking for all contexts
      if (this.enableTracking)
        this.analyticsService.trackEvent(
          POSTHOG_KEYS.HOST_PREFERENCE_UPDATED,
          POSTHOG_CATEGORIES.USER,
          POSTHOG_ACTIONS.CLICK,
        );

      // Check if provider is connected, if so track immediately, otherwise wait for connection
      if (this.isProviderConnected(event.value)) {
        // Provider is already connected, track immediately
        this.trackCalendarChange(event.value);
      } else {
        // Provider not connected, wait for OAuth completion
        this.setAwaitingConnectionState(event.value);
      }

      this.alertService.openSuccessSnack('MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.SET_PREFERRED_CALENDAR_SUCCESS');
    } catch (error) {
      // Revert UI to previous provider since the backend update failed
      this.selectedCalendarProvider = previousProvider;
      this.emitSelectedProvider();
      this.setAwaitingConnectionState(null);
      this.alertService.openErrorSnack('MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.SET_PREFERRED_CALENDAR_ERROR');
      console.error('Failed to update calendar preferences:', error);
    }
  }

  private emitSelectedProvider(): void {
    this.calendarProviderSelected.emit(this.selectedCalendarProvider);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
